import { categories } from "@sobidogam/catalog";
import {
	type HouseholdConsumptionState,
	isRecurringPayment,
	type RecurringPayment,
	recurringPaymentDate,
	recurringPaymentRecorded,
} from "@sobidogam/domain";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import type { Draft } from "./App";
import { QuickRecord } from "./QuickRecord";

export function RecurringPayments({
	state,
	receipts,
	onChange,
	onRecord,
	onAddToCalendar,
}: {
	state: HouseholdConsumptionState;
	receipts: Receipt[];
	onChange: (next: HouseholdConsumptionState) => Promise<boolean>;
	onRecord: (draft: Draft) => Promise<boolean>;
	/** #39: absent on Apps in Toss (토스 알림 동의 경로를 쓴다). */
	onAddToCalendar?: ((payments: RecurringPayment[]) => void) | undefined;
}) {
	const [form, setForm] = useState(false);
	const [editing, setEditing] = useState<string | null>(null);
	const [name, setName] = useState("");
	const [amount, setAmount] = useState("");
	const [day, setDay] = useState("1");
	const [category, setCategory] = useState("etc-recurring");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [recording, setRecording] = useState<RecurringPayment | null>(null);
	const payments = state.recurringPayments ?? [];
	const now = new Date();
	const openForm = (payment?: RecurringPayment) => {
		setEditing(payment?.id ?? null);
		setName(payment?.name ?? "");
		setAmount(payment ? String(payment.amount) : "");
		setDay(String(payment?.dayOfMonth ?? 1));
		setCategory(payment?.categoryId ?? "etc-recurring");
		setForm(true);
		setRecording(null);
		setError("");
	};
	const save = async (next: RecurringPayment[]) => {
		if (busy) return false;
		setBusy(true);
		setError("");
		try {
			const ok = await onChange({ ...state, recurringPayments: next });
			if (!ok)
				setError(
					"저장하지 못했어요. 입력을 그대로 두었으니 다시 시도해 주세요.",
				);
			return ok;
		} finally {
			setBusy(false);
		}
	};
	return (
		<section className="recurring-payments" aria-label="정기 결제 예정표">
			<p className="field-hint">
				내가 등록한 예정이에요. 실제 결제는 확인한 뒤 소비 내역에 넣어요.
			</p>
			{error && (
				<p className="form-error" role="alert">
					{error}
				</p>
			)}
			<button
				className="button button-small"
				type="button"
				onClick={() => openForm()}
			>
				정기 결제 추가
			</button>
			{onAddToCalendar &&
			payments.filter((payment) => !payment.paused).length > 1 ? (
				<button
					className="quiet-button"
					type="button"
					onClick={() =>
						onAddToCalendar(payments.filter((payment) => !payment.paused))
					}
				>
					예정 전부 내 달력에 넣기
				</button>
			) : null}
			{payments.length === 0 && !form && (
				<div className="household-empty">
					<h2>매달 나가는 비용을 기억해요</h2>
					<p>구독·통신비처럼 반복되는 결제를 예정표에 남겨 보세요.</p>
				</div>
			)}
			{payments.map((payment) => {
				const recorded = recurringPaymentRecorded(payment.id, receipts, now);
				return (
					<article className="recurring-card" key={payment.id}>
						<strong>{payment.name}</strong>
						<span>
							{payment.amount.toLocaleString("ko-KR")}원 · 매달{" "}
							{payment.dayOfMonth}일
						</span>
						<p>
							{payment.paused
								? "일시중지"
								: recorded
									? "이번 달 기록 완료"
									: `${recurringPaymentDate(payment, now)} 결제 예정`}
						</p>
						<div className="item-secondary-actions">
							<button
								type="button"
								disabled={busy || recorded || payment.paused}
								onClick={() => {
									setRecording(payment);
									setForm(false);
								}}
							>
								결제했어요
							</button>
							<button
								type="button"
								disabled={busy}
								onClick={() => openForm(payment)}
							>
								수정
							</button>
							<button
								type="button"
								disabled={busy}
								onClick={() =>
									void save(
										payments.map((item) =>
											item.id === payment.id
												? { ...item, paused: !item.paused }
												: item,
										),
									)
								}
							>
								{payment.paused ? "다시 시작" : "일시중지"}
							</button>
							{onAddToCalendar && !payment.paused ? (
								<button
									type="button"
									onClick={() => onAddToCalendar([payment])}
								>
									달력에 넣기
								</button>
							) : null}
							<button
								className="quiet-button"
								type="button"
								disabled={busy}
								onClick={() => {
									if (
										confirm(
											`${payment.name} 예정만 삭제할까요? 기존 소비 내역은 남아요.`,
										)
									)
										void save(
											payments.filter((item) => item.id !== payment.id),
										);
								}}
							>
								삭제
							</button>
						</div>
					</article>
				);
			})}
			{form && (
				<form
					className="recurring-form"
					onSubmit={async (event) => {
						event.preventDefault();
						const payment: RecurringPayment = {
							id: editing ?? crypto.randomUUID(),
							name: name.trim(),
							amount: Number(amount),
							dayOfMonth: Number(day),
							categoryId: category,
							paused:
								payments.find((item) => item.id === editing)?.paused ?? false,
						};
						if (!isRecurringPayment(payment)) {
							setError("이름과 1원 이상의 금액을 확인해 주세요.");
							return;
						}
						if (
							await save(
								editing
									? payments.map((item) =>
											item.id === editing ? payment : item,
										)
									: [...payments, payment],
							)
						)
							setForm(false);
					}}
				>
					<h2>{editing ? "정기 결제 수정" : "새 정기 결제"}</h2>
					<label>
						결제 이름
						<input
							required
							maxLength={80}
							value={name}
							onChange={(event) => setName(event.target.value)}
							placeholder="예: 휴대폰 요금"
						/>
					</label>
					<label>
						예정 금액
						<input
							required
							inputMode="numeric"
							value={amount}
							onChange={(event) =>
								setAmount(event.target.value.replace(/[^0-9]/g, ""))
							}
						/>
					</label>
					<label>
						매달 결제일
						<select
							value={day}
							onChange={(event) => setDay(event.target.value)}
						>
							{Array.from({ length: 31 }, (_, i) => i + 1).map((dayNumber) => (
								<option key={dayNumber} value={dayNumber}>
									{dayNumber}일
								</option>
							))}
						</select>
					</label>
					<label>
						카테고리
						<select
							value={category}
							onChange={(event) => setCategory(event.target.value)}
						>
							{categories
								.filter((item) => !item.sensitive)
								.map((item) => (
									<option key={item.categoryId} value={item.categoryId}>
										{item.nameKo}
									</option>
								))}
						</select>
					</label>
					<p className="field-hint">
						31일이 없는 달은 그달 마지막 날로 보여요.
					</p>
					<button className="button" type="submit" disabled={busy}>
						{busy ? "저장 중…" : "예정 저장"}
					</button>
					<button
						className="quiet-button"
						type="button"
						disabled={busy}
						onClick={() => setForm(false)}
					>
						취소
					</button>
				</form>
			)}
			{recording && (
				<section className="recurring-record">
					<h2>{recording.name} 실제 결제</h2>
					<QuickRecord
						key={recording.id}
						recurring={recording}
						receipts={receipts}
						household={state}
						onSave={onRecord}
					/>
					<button
						className="quiet-button"
						type="button"
						onClick={() => setRecording(null)}
					>
						예정표로 돌아가기
					</button>
				</section>
			)}
		</section>
	);
}
